import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { isPlaceholderPseudo, useChangePseudo, useCurrentProfile, } from './api';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input, Label, FieldError, FieldHint } from '@/components/ui/Input';
import { useToast } from '@/components/ui/Toast';
const errorMessages = {
    pseudo_length: 'Entre 3 et 24 caractères.',
    pseudo_format: 'Lettres, chiffres, _ et - uniquement.',
    pseudo_taken: 'Ce pseudo est déjà pris, essaie une variante.',
    auth_required: 'Connexion requise.',
    profile_not_found: 'Profil introuvable.',
    unknown: 'Une erreur est survenue.',
};
const SKIP_KEY = 'onboarding-pseudo-skipped';
/**
 * Modal d'onboarding qui s'ouvre automatiquement quand le profil courant a
 * encore le pseudo placeholder "Olympien<hex>" généré au signup.
 *
 * Le joueur peut le remplir maintenant ou skipper (le choix est mémorisé en
 * localStorage pour ne pas le harceler — il pourra toujours changer son
 * pseudo depuis la page profil).
 */
export function OnboardingPseudoModal() {
    const { data: profile } = useCurrentProfile();
    const { mutateAsync, isPending } = useChangePseudo();
    const { toast } = useToast();
    const [open, setOpen] = useState(false);
    const [value, setValue] = useState('');
    const [error, setError] = useState(null);
    useEffect(() => {
        if (!profile)
            return;
        if (!isPlaceholderPseudo(profile.pseudo))
            return;
        if (typeof window !== 'undefined' && window.localStorage.getItem(SKIP_KEY) === '1')
            return;
        setOpen(true);
    }, [profile?.pseudo]);
    async function onSubmit(e) {
        e.preventDefault();
        setError(null);
        try {
            await mutateAsync(value);
            toast({ tone: 'success', title: 'Bienvenue !', description: `Tu joues sous "${value}".` });
            setOpen(false);
        }
        catch (err) {
            const code = err?.code ?? 'unknown';
            setError(errorMessages[code]);
        }
    }
    function skip() {
        try {
            window.localStorage.setItem(SKIP_KEY, '1');
        }
        catch {
            /* ignore */
        }
        setOpen(false);
    }
    return (_jsx(Modal, { open: open, onOpenChange: (o) => {
            if (!o)
                skip();
        }, title: _jsxs("span", { className: "inline-flex items-center gap-2", children: [_jsx(Sparkles, { className: "h-5 w-5 text-flame-400" }), "Choisis ton pseudo"] }), description: "Tu peux jouer en invit\u00E9, mais un pseudo te rendra reconnaissable de tes amis.", hideCloseButton: true, size: "md", footer: _jsxs(_Fragment, { children: [_jsx(Button, { variant: "ghost", onClick: skip, children: "Plus tard" }), _jsx(Button, { variant: "primary", form: "onboarding-pseudo", type: "submit", isLoading: isPending, children: "Valider" })] }), children: _jsxs("form", { id: "onboarding-pseudo", onSubmit: (e) => void onSubmit(e), className: "space-y-2", children: [_jsx(Label, { htmlFor: "onboarding-pseudo-input", required: true, children: "Ton pseudo" }), _jsx(Input, { id: "onboarding-pseudo-input", autoFocus: true, value: value, onChange: (e) => setValue(e.target.value), maxLength: 24, invalid: !!error, placeholder: "Damien_Alfa" }), error ? _jsx(FieldError, { children: error }) : _jsx(FieldHint, { children: "3 \u00E0 24 caract\u00E8res. Lettres, chiffres, _ et -." })] }) }));
}
